import startCase from 'lodash/startCase';

import PopperTooltip from 'shared/ui/Popper/PopperTooltip';
import { stringifyError } from 'shared/functions';
import SpinnerSmall from 'shared/ui/Spinner/SpinnerSmall';
import { WithCode } from '@eva/types/dto';

import {
  FormEntity,
  ItemsPlaceholder,
  type GroupRowState,
  type RowState,
  type ControlRenderProps,
  type GetEntityControlRendererParams,
  type TableState,
  type GroupedTableClassNames,
} from 'components/GroupedList';

export const isWithCode = <T, E extends WithCode<T>>(entity: E | unknown): entity is WithCode<T> =>
  Boolean((entity as WithCode<T>)?.code);

export interface WithChildren<C> {
  children?: C[];
}

export const getDefaultInlineEntityForm = <E,>(
  entity: E,
  action: (...args: any[]) => Promise<any>,
  updateRowState: (changes: Partial<RowState>) => void,
  params: GetEntityControlRendererParams,
) => (
  <FormEntity
    initialValues={params.createMode ? {} : entity}
    entityIdKey={params.entityIdKey}
    entityTypeKey={params.entityTypeKey}
    entityLabelKey={params.entityLabelKey}
    onSubmit={(values: any) =>
      action(values).then(() =>
        updateRowState({
          inlineForm: null,
        }),
      )
    }
    onCancel={() =>
      updateRowState({
        inlineForm: null,
      })
    }
  />
);

export const getSaveEntityFormRenderer = (params: GetEntityControlRendererParams) => {
  const stateFieldKey = params.formPlacement === 'inline' ? 'inlineForm' : 'regularForm';

  return <E, G>({ entity, action, updateRowState }: ControlRenderProps<E, G, GroupRowState>) => {
    if (!action) {
      return null;
    }

    return (
      <FormEntity
        initialValues={params.createMode ? {} : entity}
        entityIdKey={params.entityIdKey}
        entityTypeKey={params.entityTypeKey}
        entityLabelKey={params.entityLabelKey}
        onSubmit={(values: any) =>
          action(values).then(() =>
            updateRowState({
              [stateFieldKey]: null,
            }),
          )
        }
        onCancel={() =>
          updateRowState({
            [stateFieldKey]: null,
          })
        }
      />
    );
  };
};

export const getCreateGroupControlRenderer = (params: GetEntityControlRendererParams) => {
  const entityLabel = startCase(params.entityTypeKey);
  const buttonDescription = `Add ${entityLabel}`;

  const formRenderer = getSaveEntityFormRenderer({
    ...params,
    createMode: true,
  });

  const stateFieldKey = params.formPlacement === 'inline' ? 'inlineForm' : 'regularForm';

  return <E, G>(renderProps: ControlRenderProps<E, G, TableState>) => {
    const { state, updateRowState } = renderProps;

    const form = formRenderer(renderProps);

    return (
      <PopperTooltip placement="top" overlay={translate(buttonDescription)}>
        <button
          type="button"
          disabled={state?.inProgress}
          className="btn btn-sm no-padding margin-left btn-box-tool add-button"
          onClick={() => updateRowState({ [stateFieldKey]: form })}
        >
          <i className="lnr lnr-plus-circle text-success" />
          <span className="margin-left text-success">{buttonDescription}</span>
        </button>
      </PopperTooltip>
    );
  };
};

export const getCreateEntityControlRenderer = (params: GetEntityControlRendererParams) => {
  const entityLabel = startCase(params.entityTypeKey);
  const tooltipOverlay = `Add ${entityLabel}`;

  const formRenderer = getSaveEntityFormRenderer({
    ...params,
    createMode: true,
  });

  const stateFieldKey = params.formPlacement === 'inline' ? 'inlineForm' : 'regularForm';

  return <E, G>(renderProps: ControlRenderProps<E, G, GroupRowState>) => {
    const { entity, action, state, rowState, updateRowState } = renderProps;

    const form = formRenderer({
      ...renderProps,
      action: (values: Partial<E>) => {
        const body = {
          ...values,
          [params.groupIdKey]: entity[params.groupIdKey] || null,
        };
        return action(body);
      },
    });

    const disabled = state?.inProgress || rowState.isConfirming || rowState.isDragging;

    const entityId = entity[params.entityIdKey] || 'null';

    return (
      <PopperTooltip placement="top" overlay={translate(tooltipOverlay)}>
        <button
          type="button"
          disabled={disabled}
          className="btn btn-sm no-padding margin-left btn-box-tool add-button"
          data-testId={`create-item-${entityId}`}
          onClick={() => updateRowState({ [stateFieldKey]: form })}
        >
          <i className="lnr lnr-plus-circle text-success" />
        </button>
      </PopperTooltip>
    );
  };
};

export const getUpdateEntityControlRenderer = (params: GetEntityControlRendererParams) => {
  const entityLabel = startCase(params.entityTypeKey);
  const tooltipOverlay = `Edit ${entityLabel}`;

  const formRenderer = getSaveEntityFormRenderer(params);

  const stateFieldKey = params.formPlacement === 'inline' ? 'inlineForm' : 'regularForm';

  return <E, G>(renderProps: ControlRenderProps<E, G>) => {
    const { entity, state, rowState, getEntityId, updateRowState } = renderProps;

    if (!getEntityId(entity)) {
      return null;
    }
    if (rowState.isConfirming) {
      return null;
    }

    const form = formRenderer(renderProps);

    const entityId = getEntityId(entity);

    const disabled = state?.inProgress || rowState.isConfirming || rowState.isDragging;

    return (
      <PopperTooltip placement="top" overlay={translate(tooltipOverlay)}>
        <button
          type="button"
          disabled={disabled}
          data-testId={`create-item-${entityId}`}
          className="btn btn-sm no-padding margin-left btn-box-tool"
          onClick={() => updateRowState({ [stateFieldKey]: form })}
        >
          <i className="lnr lnr-pencil" />
        </button>
      </PopperTooltip>
    );
  };
};

export const getDeleteEntityControlRenderer =
  <E, G>(confirmationMessage?: ((entity: E) => string | null) | string | null) =>
  ({ entity, action, state, rowState, updateState, getEntityId, updateRowState }: ControlRenderProps<E, G>) => {
    const containsCode = isWithCode(entity);
    if (containsCode) {
      return <div />;
    }

    const entityId = getEntityId(entity);

    if (!entityId) {
      return null;
    }

    const message = typeof confirmationMessage === 'function' ? confirmationMessage(entity) : confirmationMessage;

    if (rowState.isConfirming) {
      return (
        <div>
          <PopperTooltip placement="top" overlay={translate('Confirm')}>
            <button
              type="button"
              disabled={state?.inProgress}
              className="btn btn-sm btn-box-tool no-padding margin-left text-danger"
              onClick={() =>
                action(entity)
                  .catch((err) => {
                    const error = stringifyError(err);
                    updateRowState({ error });
                  })
                  .finally(() => {
                    updateRowState({ message: null, isConfirming: false });
                  })
              }
            >
              <i className="lnr lnr-trash" />
            </button>
          </PopperTooltip>
          <PopperTooltip placement="top" overlay={translate('Cancel')}>
            <button
              type="button"
              disabled={state?.inProgress}
              className="btn btn-sm btn-box-tool no-padding margin-left text-muted"
              onClick={() => {
                updateState({ isConfirming: false });
                updateRowState({
                  error: null,
                  message: null,
                  isConfirming: false,
                });
              }}
            >
              <i className="lnr lnr-cross" />
            </button>
          </PopperTooltip>
        </div>
      );
    }

    return (
      <div>
        <PopperTooltip placement="top" overlay={translate('Delete')}>
          <button
            type="button"
            disabled={containsCode || rowState.isDragging}
            data-testid={`delete-item-${entityId}`}
            className="btn btn-sm no-padding margin-left btn-box-tool text-danger"
            onClick={() => {
              updateState({ isConfirming: true });
              updateRowState({
                error: null,
                isConfirming: true,
                message,
              });
            }}
          >
            <i className="lnr lnr-trash" />
          </button>
        </PopperTooltip>
      </div>
    );
  };

export const renderEnableDisableEntityControl = <E extends { disabled?: boolean }, G>({
  entity,
  action,
  state,
  rowState,
  getEntityId,
}: ControlRenderProps<E, G>) => {
  const entityId = getEntityId(entity);

  if (!entityId) {
    return null;
  }

  const disabled = state?.inProgress || rowState.isDragging || rowState.isConfirming;

  return (
    <PopperTooltip placement="top" overlay={translate(entity.disabled ? 'Unarchive' : 'Archive')}>
      <button
        type="button"
        disabled={disabled}
        data-testid={`archive-item-${entityId}`}
        className={`btn btn-sm no-padding margin-left btn-tool-box ${entity.disabled ? 'text-dark-blue' : ''}`}
        onClick={() => action(entity)}
      >
        {state?.inProgress ? <SpinnerSmall /> : <i className="ll-icon ll-archive" />}
      </button>
    </PopperTooltip>
  );
};

export const getDefaultItemsPlaceholderRenderer =
  <I,>(params: GetEntityControlRendererParams, action: (entity: I) => Promise<unknown>) =>
  (
    groupId: number,
    _itemStates: { [key: number]: RowState },
    _updateItemState: (item: I, updates: Partial<RowState>) => void,
    classNames: GroupedTableClassNames,
  ) => (
    <ItemsPlaceholder
      classNames={classNames}
      entityIdKey={params.entityIdKey}
      entityTypeKey={params.entityTypeKey}
      entityLabelKey={params.entityLabelKey}
      onCreateItem={(item: I) =>
        action({
          [params.groupIdKey]: groupId,
          ...item,
        })
      }
    />
  );
